import { Inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  PriceListApi,
  FeatureApi,
  FeatureDTO,
  PriceListDTO,
} from '@lkp/parking-sdk';
import { PRICE_LIST_API, FEATURE_API } from '../../providers/api.module';
import * as Actions from './pricelist.actions';
import { tap } from 'rxjs';

export interface PriceListStateModel {
  pricelists: PriceListDTO[];
  selectedPriceList: PriceListDTO | null;
  feature: FeatureDTO[]; //Tillägstjänster
}

@State<PriceListStateModel>({
  name: 'pricelist',
  defaults: {
    pricelists: [],
    selectedPriceList: null,
    feature: [],
  },
})
@Injectable()
export class PricelistState {
  constructor(
    @Inject(PRICE_LIST_API) readonly priceListApi: PriceListApi,
    @Inject(FEATURE_API) readonly featureApi: FeatureApi
  ) {}

  @Selector()
  static currentPriceLists(state: PriceListStateModel) {
    return state.pricelists;
  }

  @Selector()
  static selectedPriceLists(state: PriceListStateModel) {
    return state.selectedPriceList;
  }

  @Selector()
  static currentFeatures(state: PriceListStateModel) {
    return state.feature;
  }

  @Action(Actions.GetPricelists)
  getPriceLists(context: StateContext<PriceListStateModel>) {
    return this.priceListApi.getPriceListsAdmin().pipe(
      tap((pricelists) => {
        context.patchState({ pricelists: pricelists });
      })
    );
  }

  @Action(Actions.GetPricelistById)
  getPriceList(
    context: StateContext<PriceListStateModel>,
    action: Actions.GetPricelistById
  ) {
    return this.priceListApi
      .getPriceListAdmin({ priceListId: action.priceListId })
      .pipe(
        tap((priceList) => {
          context.patchState({ selectedPriceList: priceList });
        })
      );
  }

  @Action(Actions.CreatePricelist)
  createPricelist(
    context: StateContext<PriceListStateModel>,
    action: Actions.CreatePricelist
  ) {
    return this.priceListApi
      .createPriceListAdmin({ priceListDTO: action.priceList })
      .pipe(
        tap({
          next: (res) => {
            context.dispatch(new Actions.GetPricelists());
            context.patchState({ selectedPriceList: res });
          },
          error: (error) => {
            console.error('Could not create pricelist: ', error);
          },
        })
      );
  }

  @Action(Actions.DeletePricelist)
  deletePricelist(
    context: StateContext<PriceListStateModel>,
    action: Actions.DeletePricelist
  ) {
    return this.priceListApi
      .deletePriceListAdmin({ priceListId: action.priceListId })
      .pipe(
        tap(() => {
          const pricelists = context
            .getState()
            .pricelists.filter(
              (priceList) => priceList.priceListId !== action.priceListId
            );
          context.patchState({
            pricelists,
            selectedPriceList: null, // Clear selectedPriceList after deletion
          });
        })
      );
  }

  @Action(Actions.UpdatePriceList)
  updatePricelist(
    context: StateContext<PriceListStateModel>,
    action: Actions.UpdatePriceList
  ) {
    const priceListId = action.priceList.priceListId;

    if (priceListId) {
      return this.priceListApi
        .updatePriceListAdmin({ priceListId, priceListDTO: action.priceList })
        .pipe(
          tap((updatedData) => {
            context.patchState({
              selectedPriceList: updatedData,
            });
            context.dispatch(new Actions.GetPricelists());
          })
        );
    } else {
      return undefined;
    }
  }

  @Action(Actions.GetFeatures)
  getFeatures(
    context: StateContext<PriceListStateModel>,
    action: Actions.GetFeatures
  ) {
    return this.featureApi
      .findFeaturesDeprecated({ mainFeatureType: action.mainFeatureType })
      .pipe(
        tap((features) => {
          context.patchState({ feature: features });
        })
      );
  }

  @Action(Actions.GetAllFeatures)
  getAllFeatures(context: StateContext<PriceListStateModel>) {
    return this.featureApi.getFeaturesAdmin().pipe(
      tap((features) => {
        context.patchState({ feature: features });
      })
    );
  }
}
